import { useGetUserOrganizationsQuery } from '@/state';
import type { OrganizationMembership } from '@/types';
import { useMemo } from 'react';
import { useTenant } from './use-tenant';

type UseGetUserCurrentOrganizationArgs = {
  // If parent already fetched orgs, pass them here to avoid a second hook call
  organizations?: OrganizationMembership[] | null;
  // Optional: pass parent's loading state if you want this hook to reflect it
  isLoadingOrganizations?: boolean;
  isFetchingOrganizations?: boolean;
};

export const useGetUserCurrentOrganization = (
  args?: UseGetUserCurrentOrganizationArgs
) => {
  const { tenant } = useTenant();

  const providedOrgs = args?.organizations ?? null;
  const hasProvidedOrgsArray = Array.isArray(providedOrgs);
  const providedOrgsLength = hasProvidedOrgsArray ? providedOrgs.length : 0;

  /**
   * Fetch only if:
   * - parent didn't provide orgs, OR
   * - parent provided an empty array AND parent says it's still loading/fetching (optional), OR
   * - parent provided empty array with no loading flags (we assume "empty means empty") -> no fetch
   *
   * If you want "empty array should always fetch", set shouldFetch = !hasProvidedOrgsArray || providedOrgsLength === 0
   */
  const parentIsLoading =
    !!args?.isLoadingOrganizations || !!args?.isFetchingOrganizations;

  const shouldFetch =
    !hasProvidedOrgsArray || (providedOrgsLength === 0 && parentIsLoading);

  const {
    data: fetchedOrgs = [],
    isLoading: isLoadingFetched,
    isFetching: isFetchingFetched,
    error,
  } = useGetUserOrganizationsQuery(undefined, {
    skip: !shouldFetch,
  });

  const orgs: OrganizationMembership[] = (
    hasProvidedOrgsArray ? providedOrgs : fetchedOrgs
  ) as OrganizationMembership[];

  // If parent provided loading flags, prefer those; otherwise use fetched flags
  const isLoading =
    args?.isLoadingOrganizations ?? (shouldFetch ? isLoadingFetched : false);
  const isFetching =
    args?.isFetchingOrganizations ?? (shouldFetch ? isFetchingFetched : false);

  const activeOrg = useMemo(() => {
    if (!tenant) return null;
    return orgs.find((o) => o.client?.schema_name === tenant) ?? null;
  }, [tenant, orgs]);

  return {
    tenant,
    orgs,
    activeOrg,
    isLoading,
    isFetching,
    error,
    roles: activeOrg?.user_roles ?? [],
  };
};
